import {
  ModalError,
  ModalLoading,
  Modal,
  ModalHeader,
  ModalContent,
  Button,
} from '@clearcover/atlas';
import {
  CoveredPartyPayloadType,
  CreateOrUpdateDriverResponseType,
  StudentStatusType,
  VehicleType,
  CalculateDriverStatusResponse,
} from '@clearcover/core-app-types';
import { Formik } from 'formik';
import React, { FC, useContext, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { env } from 'Shared/constants/env';

import { DriverType } from '@clearcover/core-app-types';
import { editDriverReportPermissionsPolicy } from 'Admin/authPolicies';
import { draftEndpointModifier, PolicyContext } from 'Policies/utils';
import {
  FormModalContent,
  FormModalFooter,
  FormModalHeader,
  FormModalUnderWritingErrors,
} from 'Shared/components';
import { useUnderwritingErrors } from 'Shared/hooks/useUnderwritingErrors';
import { useLazyApi } from 'Shared/services/Api';
import { useNewApiV2 } from 'Shared/services/Api/makeApiHttp';
import { formatDateShort, formatMoney, formatUsStateCode } from 'Shared/utils';
import { motion } from 'framer-motion';
import {
  DriverAttributes,
  DriverInfo,
  DriverReports,
  DriverLicense,
  FinancialResponsibilityCertificate,
} from '.';
import { DriverMilitaryInfo } from './DriverMilitaryInfo';
import { DriverVehicleAssignment } from './DriverVehicleAssignment';
import {
  createDriverYupValidation,
  makeCreateUpdateDriverPayload,
} from './utils';
import { DriverStatusValidation } from './DriverStatusValidation';
import { convertToToggleValue } from 'Policies/utils/helpers';

import styles from './styles.module.css';

export type DriverInfoOptionsType = {
  gender: Record<string, string>;
  licenseStatus: Record<string, string>;
  maritalStatus: Record<string, string>;
  relationToApplicant: Record<string, string>;
  licenseReportStatus: Record<string, string>;
  licenseUserReportedStatus: Record<string, string>;
};

export type DriverAttributeOptionsType = {
  educationLevel?: Record<string, string>;
  militaryRank: Record<string, string>;
  occupations: Record<string, string>;
};

export type DriverMilitaryOptionsType = {
  enabled?: boolean;
  militaryRank: Record<string, string>;
  militaryStatus: Record<string, string>;
  allowDependentDiscount: boolean;
};

export type DriverOptionQuestionsConfigType = {
  askForLicenseType: boolean;
  askHasAccessToVehicles: boolean;
  askHasOwnInsurancePolicy: boolean;
  askLivesInHousehold: boolean;
  askStateResidencyDetails: boolean;
  askForUserReportedLicenseStatus: boolean;
  askForOccupation?: boolean;
  askForEducation?: boolean;
  askForAgeFirstLicensed?: boolean;
};

export type ExcludedDriverOptionQuestionsConfigType = {
  askForGender: boolean;
  askForMaritalStatus: boolean;
};

export type DriverOptionsResponse = {
  info: DriverInfoOptionsType;
  attributes: DriverAttributeOptionsType;
  military: DriverMilitaryOptionsType;
  student: {
    allowGoodStudentDiscount: boolean;
    studentStatus: Record<StudentStatusType, string>;
  };
  driverOptionsQuestionsConfig: DriverOptionQuestionsConfigType;
  excludedDriverOptionsQuestionsConfig: ExcludedDriverOptionQuestionsConfigType;
  priorPolicy: {
    canChallenge: boolean;
  };
  accidents: {
    assumeAtFault: boolean;
  };
};

export type DriverFormType = {
  close: () => void;
  type: 'create' | 'update';
  driver?: DriverType;
  vehicles: Array<VehicleType>;
};

export type DriverStatusType =
  | 'named_insured'
  | 'covered_person'
  | 'listed_person'
  | 'excluded_person'
  | undefined;

export const GET_DRIVER_VEHICLES_TO_PAYLOAD: CoveredPartyPayloadType = (
  vehicles: VehicleType[],
  data: DriverOptionsResponse,
  driver?: DriverType,
  region?: string
) => {
  return {
    identifier: driver?.id ?? undefined,
    policyPartyRelationship:
      driver?.policyPartyRelationship ?? 'covered_person',
    info: {
      firstName: driver?.person.name.firstName ?? '',
      middleInitial: driver?.person.name.middleName ?? '',
      lastName: driver?.person.name.lastName ?? '',
      suffix: driver?.person.suffix ?? '',
      dateOfBirth: driver?.person.dateOfBirth
        ? formatDateShort(driver.person.dateOfBirth)
        : '',
      gender: driver?.person.gender ?? '',
      maritalStatus: driver?.person.maritalStatus ?? '',
      relationToApplicant: driver?.relationToApplicant ?? '',
      exposureDetails: {
        livesWithPolicyholder: convertToToggleValue(
          driver?.exposureDetails?.livesWithPolicyholder
        ),
        hasAccessToVehicles: convertToToggleValue(
          driver?.exposureDetails.hasAccessToVehicles
        ),
        hasOwnAutoPolicy: convertToToggleValue(
          driver?.exposureDetails.hasOwnAutoPolicy
        ),
      },
      licenseNumber: driver?.driversLicense.number ?? '',
      licenseState:
        driver?.driversLicense.state ?? formatUsStateCode(region) ?? '',
      ...(data.driverOptionsQuestionsConfig.askForUserReportedLicenseStatus
        ? {
            licenseUserReportedStatus:
              driver?.driversLicense?.userReportedStatus,
          }
        : {
            licenseType: driver?.driversLicense.type ?? 'us_license',
            licenseStatus: driver?.driversLicense.status.toLowerCase(),
          }),
    },
    assignedVehicles:
      driver?.assignedVehicles ?? vehicles.map((v) => v.identifier),
    attributes: {
      educationLevel: driver
        ? (Object.keys(data.attributes.educationLevel ?? []).find(
            (educationLevel) => {
              return educationLevel
                .toLowerCase()
                .startsWith(
                  driver.attributes.educationLevel.value
                    .replace(/\s/g, '')
                    .toLowerCase()
                );
            }
          ) ?? '')
        : data.driverOptionsQuestionsConfig.askForEducation !== false
          ? ''
          : undefined,
      occupation: driver?.attributes.occupation.value ?? '',
      ageFirstLicensed: driver?.ageFirstLicensed ?? '',
      intDrivingExperiences: '',
      homeowner:
        driver?.attributes.homeowner.value === 'Yes' ? 'true' : 'false',
      inStateResidencyDuration:
        driver?.residencyDetails?.inStateResidencyDuration,
      isOutOfStateStudent: driver?.residencyDetails?.isOutOfStateStudent
        ? 'true'
        : 'false',
      isStateResidentWithOutOfStateHomes: driver?.residencyDetails
        ?.isStateResidentWithOutOfStateHomes
        ? 'true'
        : 'false',
      hasVehicleWhileOutOfStateStudent: driver?.residencyDetails
        ?.hasVehicleWhileOutOfStateStudent
        ? 'true'
        : 'false',
    },
    military: driver
      ? {
          militaryStatus: driver.military.militaryStatus.dataValue,
          rank:
            driver.military.militaryRank.dataValue !== ''
              ? driver.attributes.militaryRank.value
              : '',
          isDeployed: driver.military.isDeployed.dataValue ? 'true' : 'false',
          isCertifiedMilitaryDependent: driver.military
            .dependentOfActiveMilitary?.dataValue
            ? 'true'
            : 'false',
        }
      : data.military.enabled === false
        ? undefined
        : {
            militaryStatus: '',
            rank: '',
            isDeployed: 'false',
            isCertifiedMilitaryDependent: 'false',
          },
    student: {
      goodStudentStatus: driver?.student.goodStudentStatus.dataValue ?? 'none',
    },
    reports: {
      sr22s:
        driver?.sr22s.reduce(
          (acc, sr22) => ({
            ...acc,
            [sr22.id]: {
              challenge: sr22.challenge,
              startDate: sr22.startDate,
              endDate: sr22.endDate,
            },
          }),
          {}
        ) ?? {},
      accidents:
        driver?.accidents.reduce(
          (acc, accident) => ({
            ...acc,
            [accident.id]: {
              challenge: accident.challenge,
              accidentDate: accident.accidentDate,
              isAtFault: accident.isAtFault ? 'yes' : 'no',
              damageAmount: accident.damageAmount.length
                ? formatMoney(accident.damageAmount, {
                    includeCents: true,
                    formatWithSymbol: false,
                  })
                : '',
              classifications: accident.classifications,
            },
          }),
          {}
        ) ?? {},
      violations:
        driver?.violations.reduce(
          (acc, violation) => ({
            ...acc,
            [violation.id]: {
              challenge: violation.challenge,
              violationDate: violation.violationDate,
              convictionDate: violation.convictionDate,
              description: violation.description,
            },
          }),
          {}
        ) ?? {},
      driversLicense: !data.driverOptionsQuestionsConfig
        .askForUserReportedLicenseStatus
        ? {
            challenge: driver?.driversLicense.challenge,
            status: driver?.driversLicense.status
              ? driver?.driversLicense.status.toLowerCase()
              : undefined,
          }
        : {
            reportStatus: driver?.driversLicense.reportStatus ?? undefined,
          },
      priorCarrier: {
        challenge: driver?.priorCarrier.challenge,
        carrier: driver?.priorCarrier.carrier ?? '',
        isCurrentlyInsured: driver?.priorCarrier.wasPreviouslyInsured,
        bodyInjuryLimits: driver?.priorCarrier.bodyInjuryLimits ?? '',
        duration: {
          currentlyInsuredMonths: driver?.priorCarrier.currentlyInsuredMonths
            ? driver?.priorCarrier.currentlyInsuredMonths.toString()
            : undefined,
          daysSinceLastInsured: driver?.priorCarrier.daysSinceLastInsured
            ? driver?.priorCarrier.daysSinceLastInsured.toString()
            : undefined,
        },
      },
      priorPolicies:
        driver?.priorPolicies?.map((priorPolicy) => ({
          source: priorPolicy.source,
          currentCarrierName: priorPolicy.currentCarrierName,
          startDate: priorPolicy.startDate,
          endDate: priorPolicy.endDate,
          amBestNumber: priorPolicy.amBestNumber,
          state: priorPolicy.state,
          coverages: priorPolicy.coverages,
          policyNumber: priorPolicy.policyNumber,
          // @ts-expect-error - update types in core-app-types
          pniPolicy: priorPolicy.pniPolicy ?? false,
          challenge: priorPolicy.challenge ?? undefined,
        })) || [],
    },
    cfrs: driver?.cfrs,
  };
};

export const DriverForm: FC<DriverFormType> = ({
  close,
  type,
  driver,
  vehicles,
}) => {
  const history = useHistory();
  const { id: policyId, draftId } = useParams<{
    id: string;
    draftId: string;
  }>();

  const [driverStatus, setDriverStatus] = useState<DriverStatusType>(undefined);
  const [isDriverStatusCalculated, setIsDriverStatusCalculated] =
    useState(false);
  const [isPreSubmitModalOpen, setIsPreSubmitModalOpen] = useState(false);
  const [isUndefinedStatusModalOpen, setIsUndefinedStatusModalOpen] =
    useState(false);
  const {
    details,
    flags,
    refetchHandler,
    resourceEffectiveGrants,
    driverOptions,
  } = useContext(PolicyContext);
  const { underwritingErrors, setUnderwritingErrors, hasUnderwritingErrors } =
    useUnderwritingErrors();

  const isAddDriver = !(
    type === 'update' && driver?.driversLicense.status === 'Learner'
  );

  const [{ data, loading: isLoading, error }] =
    useNewApiV2<DriverOptionsResponse>({
      url: `/options/driver?configDiscoveryParams=${details.configDiscoveryParams}&productFilingIdentifier=${details.filingIdentifier}&isAddDriver=${isAddDriver}`,
    });

  const [, createOrUpdateDriver] = useLazyApi<CreateOrUpdateDriverResponseType>(
    {
      method: 'POST',
      url: `/policies/${policyId}/createOrUpdateDriver${draftEndpointModifier(
        details.draftType,
        flags.renewalQuotesEnabled
      )}`,
    }
  );

  const [{ loading: driverStatusIsLoading }, calculateDriverStatus] =
    useLazyApi<CalculateDriverStatusResponse>({
      method: 'POST',
      url: '/driverStatus',
    });

  const areDriverNewQuestionsConfigured =
    data?.driverOptionsQuestionsConfig.askLivesInHousehold &&
    data?.driverOptionsQuestionsConfig.askHasAccessToVehicles &&
    data?.driverOptionsQuestionsConfig.askHasOwnInsurancePolicy;

  useEffect(() => {
    setDriverStatus(driver?.policyPartyRelationship ?? 'covered_person');
  }, [driver?.policyPartyRelationship]);

  if (isLoading) {
    return <ModalLoading data-testid="driver-modal-loading" />;
  }

  const initializationError =
    error ||
    (!(data && data.info && data.attributes) &&
      new Error('Could not load form data'));

  if (initializationError) {
    return (
      <ModalError
        data-testid="driver-modal-error"
        errorMessage={initializationError.message}
        componentStack={initializationError.stack}
      />
    );
  }

  const canChallengePriorPolicy =
    driverOptions?.priorPolicy?.canChallenge ?? false;

  const DRIVER_VALIDATION = createDriverYupValidation(
    type,
    driver,
    details,
    data,
    driverStatus,
    canChallengePriorPolicy
  );

  const submitDriverForm = async ({
    values,
    setStatus,
    setSubmitting,
    bypassUnderwriting,
  }: {
    values: CoveredPartyPayloadType;
    setStatus: (arg0: any) => void;
    setSubmitting: (arg0: boolean) => void;
    bypassUnderwriting?: boolean;
  }) => {
    const payload = makeCreateUpdateDriverPayload({
      values,
      type,
      requiredAccessStatements: details.requiredAccessStatements ?? false,
      excludedDriverOptionsQuestionsConfig:
        data.excludedDriverOptionsQuestionsConfig,
      bypassUnderwriting,
    });
    return createOrUpdateDriver({ data: payload })
      .then((draft) => {
        draftId === draft.data.draftId
          ? refetchHandler()
          : history.push(`/policies/${policyId}/draft/${draft.data.draftId}`);
      })
      .catch((status) => {
        setUnderwritingErrors(status);
        setStatus(hasUnderwritingErrors(status) ? '' : status.message);
        setSubmitting(false);
      });
  };

  return (
    <Formik
      initialValues={GET_DRIVER_VEHICLES_TO_PAYLOAD(
        vehicles,
        data,
        driver,
        details?.region
      )}
      onSubmit={(values, { setStatus, setSubmitting }) => {
        if (
          (driverStatus === 'listed_person' ||
            driverStatus === 'excluded_person') &&
          isAddDriver &&
          typeof values.info.exposureDetails.hasAccessToVehicles !==
            'undefined' &&
          typeof values.info.exposureDetails.hasOwnAutoPolicy !== 'undefined' &&
          typeof values.info.exposureDetails.livesWithPolicyholder !==
            'undefined'
        ) {
          setIsPreSubmitModalOpen(true);
          return Promise.resolve();
        } else {
          // Proceed with submission if no modal needed
          return submitDriverForm({
            values,
            setStatus,
            setSubmitting,
            bypassUnderwriting: false,
          });
        }
      }}
      validationSchema={DRIVER_VALIDATION}
      validateOnMount
      validate={() => {
        if (driverStatus === undefined) {
          // This message will not be displayed to the user, but it will prevent the form from submitting
          // and can be tracked using the react developer tools in the formik object state
          return { driverStatus: 'Driver status is required' };
        }
        // No more errors will be added in this function. Validation schema will handle all errors.
        return {};
      }}
    >
      {({ values, setStatus, setSubmitting, setFieldValue }) => {
        return (
          <>
            <FormModalHeader>
              {driver ? 'Edit Driver' : 'Add Driver'}
            </FormModalHeader>
            <FormModalContent>
              <DriverFormContent
                data={data}
                policyPartyRelationship={values.policyPartyRelationship}
                driver={driver}
                vehicles={vehicles}
                driverStatus={driverStatus}
                areDriverNewQuestionsConfigured={
                  areDriverNewQuestionsConfigured
                }
                isDriverStatusCalculated={isDriverStatusCalculated}
              />
            </FormModalContent>
            <FormModalUnderWritingErrors
              errors={underwritingErrors}
              grants={resourceEffectiveGrants}
              submitIgnoringUnderwriting={() =>
                submitDriverForm({
                  values,
                  setStatus,
                  setSubmitting,
                  bypassUnderwriting: true,
                })
              }
            />
            <FormModalFooter
              identifier={
                driver?.id
                  ? `${driver?.id}-edit-driver-form`
                  : 'add-driver-form'
              }
              close={close}
            />
            {driverStatusIsLoading && (
              <div className={styles.overlay}>
                <ModalLoading />
              </div>
            )}

            {/* Driver Status Validation when driver new questions are enabled and configured */}
            {env.driverNewQuestionsEnabled &&
              areDriverNewQuestionsConfigured && (
                <DriverStatusValidation
                  values={values}
                  calculateDriverStatus={calculateDriverStatus}
                  updateDriverStatus={(value) => {
                    setDriverStatus(value);
                    setIsDriverStatusCalculated(true);
                    setFieldValue('policyPartyRelationship', value);
                    if (!value) {
                      setIsUndefinedStatusModalOpen(true);
                    }
                  }}
                  driverOptionsQuestionsConfig={
                    data.driverOptionsQuestionsConfig
                  }
                  currentDriverStatus={driverStatus}
                />
              )}

            {/* Pre-submit modal for listed-person and excluded-driver */}
            <DriverFormPreSubmitModal
              isOpen={isPreSubmitModalOpen}
              onClose={() => {
                setIsPreSubmitModalOpen(false);
                setSubmitting(false);
              }}
              onSubmit={() => {
                setIsPreSubmitModalOpen(false);
                return submitDriverForm({ values, setStatus, setSubmitting });
              }}
              driverStatus={driverStatus}
              values={values}
            />
            <DriverFormUndefinedStatusModal
              isOpen={isUndefinedStatusModalOpen}
              onClose={() => {
                setIsUndefinedStatusModalOpen(false);
              }}
            />
          </>
        );
      }}
    </Formik>
  );
};

const DriverFormPreSubmitModal: FC<{
  isOpen: boolean;
  onClose: () => void;
  onSubmit: () => void;
  driverStatus: DriverStatusType;
  values: CoveredPartyPayloadType;
}> = ({ isOpen, onClose, onSubmit, driverStatus, values }) => {
  return (
    <Modal
      controlled={true}
      open={isOpen}
      handleClose={onClose}
      data-testid="driver-form-pre-submit-modal"
    >
      {(closeModal) => {
        const driverFullName = `${values.info.firstName} ${values.info.lastName}`;

        const statusContent = {
          listed_person: {
            header: `${driverFullName} will be added to the policy as a list-only driver.`,
            content: (
              <>
                <p>
                  Based on your answer to these questions, {driverFullName}{' '}
                  needs to be added to the policy as a list-only driver.
                </p>
                <p>
                  <strong>
                    What is a listed driver? Listed drivers have permission to
                    operate the car and are eligible for coverage in case of
                    accidents or other incidents as per the policy terms but do
                    not regularly drive the insured vehicle(s).
                  </strong>
                </p>
                <p>
                  If you have any questions about our driver status assignment,
                  please review our{' '}
                  <a
                    target="_blank"
                    rel="noreferrer"
                    href="https://resources.clearcover.com/hc/en-us"
                  >
                    Agent Guidelines
                  </a>{' '}
                  in the Agent Resource Center.
                </p>
              </>
            ),
            buttonText: 'Add List-Only Driver',
          },
          excluded_person: {
            header: `This driver will be added to policy as an excluded driver.`,
            content: (
              <>
                <p>
                  Based on your answer to these questions, {driverFullName}{' '}
                  needs to be added to the policy as an excluded driver.
                </p>
                <p>
                  Excluded drivers are not covered for any claim arising from an
                  accident or incident involving vehicles insured on the policy.
                </p>
              </>
            ),
            buttonText: 'Add as excluded',
          },
        };

        const { header, content, buttonText } =
          statusContent[driverStatus as 'listed_person' | 'excluded_person'];

        return (
          <>
            <ModalHeader>
              <h3 className={styles.modalHeader}>{header}</h3>
            </ModalHeader>
            <ModalContent>
              <div className={styles.modalBody}>
                {content}
                <div className={styles.modalButtons}>
                  <Button
                    block
                    data-testid="go-back-button"
                    variant="secondary"
                    size={'md'}
                    onClick={closeModal}
                  >
                    Cancel
                  </Button>
                  <Button
                    block
                    data-testid="confirm-button"
                    variant={'primary'}
                    size={'md'}
                    isSubmitting={false}
                    onClick={onSubmit}
                  >
                    {buttonText}
                  </Button>
                </div>
              </div>
            </ModalContent>
          </>
        );
      }}
    </Modal>
  );
};

const DriverFormUndefinedStatusModal: FC<{
  isOpen: boolean;
  onClose: () => void;
}> = ({ isOpen, onClose }) => {
  return (
    <Modal
      controlled={true}
      open={isOpen}
      handleClose={onClose}
      data-testid="driver-form-undefined-status-modal"
    >
      {(closeModal) => {
        return (
          <>
            <ModalHeader>
              <h3 className={styles.modalHeader}>
                This driver cannot be added to the policy.
              </h3>
            </ModalHeader>
            <ModalContent>
              <div className={styles.modalBody}>
                <>
                  <p>
                    Based on your answers to these questions, this driver cannot
                    be added to the policy.
                  </p>
                  <p>
                    Unlicensed drivers are not accepted. Please review and try
                    again.
                  </p>
                </>
                <div className={styles.modalButtons}>
                  <Button
                    block
                    data-testid="confirm-button"
                    variant={'primary'}
                    size={'md'}
                    isSubmitting={false}
                    onClick={closeModal}
                  >
                    Continue
                  </Button>
                </div>
              </div>
            </ModalContent>
          </>
        );
      }}
    </Modal>
  );
};

const DriverFormContent: FC<{
  policyPartyRelationship: string;
  data: DriverOptionsResponse;
  driver?: DriverType;
  vehicles: Array<VehicleType>;
  driverStatus?:
    | 'named_insured'
    | 'covered_person'
    | 'listed_person'
    | 'excluded_person';
  areDriverNewQuestionsConfigured: boolean;
  isDriverStatusCalculated: boolean;
}> = ({
  policyPartyRelationship,
  data,
  driver,
  vehicles,
  driverStatus,
  areDriverNewQuestionsConfigured,
  isDriverStatusCalculated,
}) => {
  const { details, resourceEffectiveGrants } = useContext(PolicyContext);

  const canEditReports = editDriverReportPermissionsPolicy(
    resourceEffectiveGrants
  );

  const formType = driver ? 'update' : 'create';

  const progressiveAddDriverForm =
    formType === 'create' &&
    areDriverNewQuestionsConfigured &&
    env.driverNewQuestionsEnabled;

  return policyPartyRelationship === 'excluded_person' ? (
    <motion.div
      animate="open"
      initial="closed"
      exit="exit"
      data-testid="driver-form-content"
      variants={{
        closed: {
          y: -100,
          transition: { duration: 0.5 },
        },
        open: {
          transition: { duration: 0.5 },
          y: 0,
        },
        exit: {
          transition: { duration: 0.5 },
          y: 0,
        },
      }}
    >
      <DriverInfo
        options={data.info}
        driverOptionsQuestionsConfig={data.driverOptionsQuestionsConfig}
        excludedDriverOptionsQuestionsConfig={
          data.excludedDriverOptionsQuestionsConfig
        }
      />
    </motion.div>
  ) : (
    <motion.div
      animate="closed"
      initial="open"
      exit="exit"
      data-testid="driver-form-content"
      variants={{
        closed: {
          y: -50,
          transition: { duration: 0.5 },
        },
        open: {
          transition: { duration: 0.5 },
          y: 0,
        },
        exit: {
          transition: { duration: 0.5 },
          y: 0,
        },
      }}
    >
      <DriverInfo
        options={data.info}
        driverOptionsQuestionsConfig={data.driverOptionsQuestionsConfig}
        excludedDriverOptionsQuestionsConfig={
          data.excludedDriverOptionsQuestionsConfig
        }
      />
      <DriverLicense
        info={data.info}
        driverOptionsQuestionsConfig={data.driverOptionsQuestionsConfig}
      />

      {details.showDriverCfrs && <FinancialResponsibilityCertificate />}

      {/* We show these sections when */}
      {/* 1) New questions are configured, driver status was calculated and driver status is equal to covered_person */}
      {/* 2) New questions aren't configured, so driver status cannot and will not be calculated */}
      {/* This logic applies only for new drivers ("create" form type) */}
      {((progressiveAddDriverForm &&
        isDriverStatusCalculated &&
        driverStatus === 'covered_person') ||
        !progressiveAddDriverForm) && (
        <>
          <DriverAttributes
            options={data.attributes}
            askStateResidencyDetails={
              data.driverOptionsQuestionsConfig.askStateResidencyDetails
            }
            studentOptions={data.student}
            askForOccupation={
              data.driverOptionsQuestionsConfig.askForOccupation
            }
            askForEducation={data.driverOptionsQuestionsConfig.askForEducation}
          />
          {details.region === 'US-VA' ? (
            <DriverVehicleAssignment vehicles={vehicles} />
          ) : null}
          {(data.military.enabled ?? true) && (
            <DriverMilitaryInfo options={data.military} />
          )}
        </>
      )}
      {driver && canEditReports && (
        <DriverReports
          driver={driver}
          driverLicenseStatusOptions={
            data.driverOptionsQuestionsConfig.askForUserReportedLicenseStatus
              ? data.info.licenseReportStatus
              : data.info.licenseStatus
          }
        />
      )}
    </motion.div>
  );
};
